/**
 * 微信 JS-SDK 初始化授权（多页面复用）
 * @returns Promise<boolean> 授权成功返回 true，失败抛出错误
 */
const baseUrl = import.meta.env.VITE_API_BASE_URL

interface WxConfigParams {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
}

let wxInitPromise: Promise<boolean> | null = null;
let wxInitCompleted = false;

const hasWxCoreMethods = (): boolean => {
  const wxObject = (window as Window & { wx?: Record<string, unknown> }).wx;
  return (
    typeof wxObject?.config === 'function' &&
    typeof wxObject?.ready === 'function' &&
    typeof wxObject?.error === 'function'
  );
};

const hasWxScanMethod = (): boolean => {
  const wxObject = (window as Window & { wx?: Record<string, unknown> }).wx;
  return typeof wxObject?.scanQRCode === 'function';
};

const waitForWxSdk = async (timeout = 8000, interval = 100): Promise<void> => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    if (typeof window !== 'undefined' && hasWxCoreMethods()) {
      return;
    }
    await new Promise<void>((resolve) => {
      window.setTimeout(() => resolve(), interval);
    });
  }

  throw new Error('微信 JS-SDK 加载超时，请稍后重试');
};

const WX_READY_TIMEOUT = 8000;

export async function initWxSdk(): Promise<boolean> {
  if (wxInitCompleted && hasWxScanMethod()) {
    return true;
  }
  if (wxInitPromise) {
    return wxInitPromise;
  }

  wxInitPromise = (async () => {
  if (typeof window === 'undefined') {
    throw new Error('当前环境不支持微信 JS-SDK 初始化');
  }

  await waitForWxSdk();

  // 核心：获取当前页面完整 URL（含?查询参数，不含#哈希）
  const currentUrl = window.location.href.split('#')[0];
  // 调用后端接口获取签名（传当前页面 URL）
  const param = {
    localUrl: currentUrl
  };
  const response = await fetch(baseUrl + '/repair/wechat/queryConfig', {
    method: 'POST',
    showLoading: true,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(param)
  } as any);
  const result: ApiResponse<WxConfigParams | null> = await response.json();

  if (!result.success || !result.result) {
    throw new Error(result.error || '获取微信签名失败');
  }

  const configParams = result.result;
  if (!configParams.appId || !configParams.timestamp || !configParams.nonceStr || !configParams.signature) {
    throw new Error('微信签名参数不完整，无法初始化扫码能力');
  }

  // 封装 wx.ready/wx.error 为 Promise
  const initResult = await new Promise<boolean>((resolve, reject) => {
    if (!hasWxCoreMethods() || !window.wx) {
      reject(new Error('微信 JS-SDK 加载不完整，缺少核心方法（config/ready/error）'));
      return; // 终止后续执行
    }
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('微信 JS-SDK 初始化超时，请稍后重试'));
    }, WX_READY_TIMEOUT);
    window.wx.config({
      debug: false,
      appId: configParams.appId, // 公众号AppID
      timestamp: configParams.timestamp,
      nonceStr: configParams.nonceStr,
      signature: configParams.signature,
      jsApiList: ['scanQRCode'] // 按需声明接口，多个页面可扩展
    });
    // 授权成功：仅resolve，不绑定事件
    window.wx.ready(() => {
      if (settled) return;
      window.clearTimeout(timeoutId);
      if (!hasWxScanMethod()) {
        settled = true;
        reject(new Error('当前微信环境不支持扫码能力，请确认在微信内打开页面'));
        return;
      }
      settled = true;
      resolve(true);
    });
    // 授权失败：抛出具体错误
    window.wx.error((err) => {
      if (settled) return;
      window.clearTimeout(timeoutId);
      settled = true;
      reject(new Error(`授权失败：${err.errMsg}`));
    });
  });

  wxInitCompleted = initResult;
  return initResult;
  })()
    .catch((error) => {
      wxInitCompleted = false;
      throw error;
    })
    .finally(() => {
      wxInitPromise = null;
    });

  return wxInitPromise;
}